import { ScoreBadge, type ScoreThresholds } from "@aircall/blocks";

import { getColorFromRange } from "@/helpers/range/range.helpers";
import { ColorScale } from "@/helpers/range/range.helpers.decl";

export type CsatRating = "high" | "medium" | "low" | "na";

type CsatScoreProps = {
  // Null is "Not applicable"
  value: number | null;
};

/**
 * CSAT tone bands, preserving the local behaviour: high [71, 100] -> success,
 * medium [41, 71) -> warning, low [0, 41) -> destructive (not
 * destructive-strong). Values are matched top-down by their `from` bound.
 */
const CSAT_THRESHOLDS: ScoreThresholds = [
  { from: 71, tone: "success" },
  { from: 41, tone: "warning" },
  { from: 0, tone: "destructive" },
];

const csatScale: ColorScale<Exclude<CsatRating, "na">> = {
  high: [71, 100],
  medium: [41, 71],
  low: [0, 41],
};

export function getCsatRating(value: number | null): CsatRating {
  if (value === null) {
    return "na";
  }

  if (value < 0) {
    return "low";
  }

  if (value > 100) {
    return "high";
  }

  return getColorFromRange(csatScale)(value) ?? "na";
}

export type CsatTextColor =
  | "text-success"
  | "text-warning"
  | "text-destructive"
  | "text-muted-foreground";

const ratingTextColor: Record<CsatRating, CsatTextColor> = {
  high: "text-success",
  medium: "text-warning",
  low: "text-destructive",
  na: "text-muted-foreground",
};

export function getCsatRatingTextColor(rating: CsatRating): CsatTextColor {
  return ratingTextColor[rating];
}

/**
 * Thin adapter over the `@aircall/blocks` `ScoreBadge`, rendering the CSAT
 * score as a plain rounded integer (no percentage suffix) using the CSAT tone
 * bands. Finite out-of-range values are no longer clamped by the badge
 * (150 -> "150", -10 -> "-10"); only null renders the muted "--".
 */
export function CsatScore({ value }: CsatScoreProps) {
  return (
    <ScoreBadge
      value={value}
      thresholds={CSAT_THRESHOLDS}
      formatOptions={{ maximumFractionDigits: 0 }}
      data-test={`csat-score-${value}`}
    />
  );
}
